import type { ReactNode } from "react";
import { useValue } from "@tldraw/state-react";
import {
  defaultShapeSchemas,
  type TLBookmarkAsset,
  type TLBookmarkShape,
  type TLEmbedShape,
  type TLVideoShape,
} from "@tldraw/tlschema";
import { useEditor } from "../../hooks";
import { BaseBoxShapeUtil, HTMLContainer } from "../shape-util";

// Video, web link cards (bookmarks) and embedded websites.

export class VideoShapeUtil extends BaseBoxShapeUtil<TLVideoShape> {
  static override type = "video";
  static override props = defaultShapeSchemas.video.props;
  getDefaultProps(): TLVideoShape["props"] {
    return { w: 100, h: 100, assetId: null, time: 0, playing: true, autoplay: true, url: "", altText: "" } as TLVideoShape["props"];
  }
  override isAspectRatioLocked() {
    return true;
  }
  override canEdit() {
    return true;
  }
  override getIndicatorPath(shape: TLVideoShape) {
    return `M0,0H${shape.props.w}V${shape.props.h}H0Z`;
  }
  component(shape: TLVideoShape) {
    return <VideoBody shape={shape} />;
  }
  /** A video exports as a plain frame unless a subclass supplies a poster. */
  toSvg(shape: TLVideoShape): ReactNode | Promise<ReactNode> {
    return <rect width={shape.props.w} height={shape.props.h} fill="#eeeeee" stroke="#c9c4b8" />;
  }
}

function VideoBody({ shape }: { shape: TLVideoShape }) {
  const editor = useEditor();
  const src = useValue(
    "video src",
    () => {
      const asset = shape.props.assetId ? editor.getAsset(shape.props.assetId) : undefined;
      return (typeof asset?.props.src === "string" && asset.props.src) || shape.props.url || "";
    },
    [editor, shape.props.assetId, shape.props.url],
  );
  return (
    <HTMLContainer style={{ width: shape.props.w, height: shape.props.h, background: "#eee", overflow: "hidden" }}>
      {src && <video src={src} controls muted playsInline style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
    </HTMLContainer>
  );
}

export const BOOKMARK_WIDTH = 300;
export const BOOKMARK_HEIGHT = 320;

export class BookmarkShapeUtil extends BaseBoxShapeUtil<TLBookmarkShape> {
  static override type = "bookmark";
  static override props = defaultShapeSchemas.bookmark.props;
  getDefaultProps(): TLBookmarkShape["props"] {
    return { w: BOOKMARK_WIDTH, h: BOOKMARK_HEIGHT, assetId: null, url: "" } as TLBookmarkShape["props"];
  }
  override canResize() {
    return false;
  }
  override hideRotateHandle() {
    return true;
  }
  override getText(shape: TLBookmarkShape) {
    return shape.props.url;
  }
  override getIndicatorPath(shape: TLBookmarkShape) {
    return `M0,0H${shape.props.w}V${shape.props.h}H0Z`;
  }
  component(shape: TLBookmarkShape) {
    return <BookmarkBody shape={shape} />;
  }
}

const safeHref = (url: string) => (/^https?:\/\//i.test(url) ? url : undefined);

function BookmarkBody({ shape }: { shape: TLBookmarkShape }) {
  const editor = useEditor();
  const asset = useValue(
    "bookmark asset",
    () => (shape.props.assetId ? (editor.getAsset(shape.props.assetId) as TLBookmarkAsset | undefined) : undefined),
    [editor, shape.props.assetId],
  );
  const url = asset?.props.src || shape.props.url;
  const href = safeHref(url ?? "");
  let host = "";
  try {
    host = url ? new URL(url).hostname : "";
  } catch {
    host = url ?? "";
  }
  return (
    <HTMLContainer className="canvas-bookmark" style={{ width: shape.props.w, height: shape.props.h }}>
      {asset?.props.image && (
        <img className="canvas-bookmark-image" src={asset.props.image} alt="" referrerPolicy="strict-origin-when-cross-origin" />
      )}
      <div className="canvas-bookmark-copy">
        {asset?.props.title && <h3>{asset.props.title}</h3>}
        {asset?.props.description && <p>{asset.props.description}</p>}
        {href ? (
          <a
            data-canvas-interactive
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {asset?.props.favicon && <img src={asset.props.favicon} alt="" width={16} height={16} />}
            {host}
          </a>
        ) : (
          <span>{host}</span>
        )}
      </div>
    </HTMLContainer>
  );
}

/** A website that can be shown inside a board, and how to get its embed address. */
export type EmbedDefinition = {
  type: string;
  title: string;
  width: number;
  height: number;
  toEmbedUrl(url: URL): string | undefined;
};

const host = (url: URL, ...names: string[]) => names.some((n) => url.hostname === n || url.hostname.endsWith(`.${n}`));

export const EMBED_DEFINITIONS: EmbedDefinition[] = [
  {
    type: "youtube",
    title: "YouTube",
    width: 800,
    height: 450,
    toEmbedUrl(url) {
      const id = host(url, "youtu.be")
        ? url.pathname.slice(1)
        : host(url, "youtube.com")
          ? url.pathname.startsWith("/embed/")
            ? url.pathname.split("/")[2]
            : url.searchParams.get("v")
          : null;
      return id && /^[\w-]{6,}$/.test(id) ? `https://www.youtube.com/embed/${id}` : undefined;
    },
  },
  {
    type: "vimeo",
    title: "Vimeo",
    width: 640,
    height: 360,
    toEmbedUrl(url) {
      if (!host(url, "vimeo.com")) return;
      const id = url.pathname.split("/").find((part) => /^\d+$/.test(part));
      return id ? `https://player.vimeo.com/video/${id}` : undefined;
    },
  },
  {
    type: "loom",
    title: "Loom",
    width: 720,
    height: 450,
    toEmbedUrl(url) {
      const match = host(url, "loom.com") && /^\/(?:share|embed)\/([\w-]+)/.exec(url.pathname);
      return match ? `https://www.loom.com/embed/${match[1]}` : undefined;
    },
  },
  {
    type: "figma",
    title: "Figma",
    width: 720,
    height: 500,
    toEmbedUrl(url) {
      if (!host(url, "figma.com") || !/^\/(?:file|design|proto|board)\//.test(url.pathname)) return;
      return `https://www.figma.com/embed?embed_host=planroom&url=${encodeURIComponent(url.href)}`;
    },
  },
  {
    type: "google_slides",
    title: "Google Slides",
    width: 720,
    height: 450,
    toEmbedUrl(url) {
      const match = url.hostname === "docs.google.com" && /^\/presentation\/d\/([\w-]+)/.exec(url.pathname);
      return match ? `https://docs.google.com/presentation/d/${match[1]}/embed` : undefined;
    },
  },
  {
    type: "google_maps",
    title: "Google Maps",
    width: 720,
    height: 500,
    toEmbedUrl(url) {
      // Only links copied from Maps' "Embed a map" are embeddable without a key.
      return host(url, "google.com") && url.pathname.startsWith("/maps/embed") ? url.href : undefined;
    },
  },
  {
    type: "matterport",
    title: "Matterport",
    width: 800,
    height: 500,
    toEmbedUrl(url) {
      const id = host(url, "matterport.com") && url.pathname.startsWith("/show") && url.searchParams.get("m");
      return id && /^\w+$/.test(id) ? `https://my.matterport.com/show/?m=${id}` : undefined;
    },
  },
  {
    type: "sketchfab",
    title: "Sketchfab",
    width: 720,
    height: 480,
    toEmbedUrl(url) {
      if (!host(url, "sketchfab.com")) return;
      const id = /([0-9a-f]{32})/.exec(url.pathname)?.[1];
      return id ? `https://sketchfab.com/models/${id}/embed` : undefined;
    },
  },
];

/** The definition and embed address for a link, if the site can be embedded. */
export function getEmbedDefinition(link: string) {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    return undefined;
  }
  if (url.protocol !== "https:" || url.username || url.password) return undefined;
  for (const definition of EMBED_DEFINITIONS) {
    const embedUrl = definition.toEmbedUrl(url);
    if (embedUrl) return { definition, url: link, embedUrl };
  }
  return undefined;
}

export class EmbedShapeUtil extends BaseBoxShapeUtil<TLEmbedShape> {
  static override type = "embed";
  static override props = defaultShapeSchemas.embed.props;
  getDefaultProps(): TLEmbedShape["props"] {
    return { w: 300, h: 300, url: "" } as TLEmbedShape["props"];
  }
  /** Same lookup the app uses to check a link before saving it on an embed. */
  getEmbedDefinition(url: string) {
    return getEmbedDefinition(url);
  }
  override canEdit() {
    return true;
  }
  override getText(shape: TLEmbedShape) {
    return shape.props.url;
  }
  override getIndicatorPath(shape: TLEmbedShape) {
    return `M0,0H${shape.props.w}V${shape.props.h}H0Z`;
  }
  component(shape: TLEmbedShape) {
    return <EmbedBody shape={shape} />;
  }
  /** Embedded pages cannot be drawn into an export; show what they link to. */
  toSvg(shape: TLEmbedShape) {
    const title = getEmbedDefinition(shape.props.url)?.definition.title ?? "Web page";
    return (
      <g>
        <rect width={shape.props.w} height={shape.props.h} fill="#ffffff" stroke="#c9c4b8" />
        <text x={16} y={32} fontSize={16} fontFamily="sans-serif" fill="#1d1d1d">
          {title}
        </text>
        <text x={16} y={56} fontSize={12} fontFamily="sans-serif" fill="#6b665a">
          {shape.props.url.slice(0, 80)}
        </text>
      </g>
    );
  }
}

function EmbedBody({ shape }: { shape: TLEmbedShape }) {
  const editor = useEditor();
  const editing = useValue("embed editing", () => editor.getEditingShapeId() === shape.id, [editor, shape.id]);
  const embed = getEmbedDefinition(shape.props.url);
  const href = safeHref(shape.props.url);
  return (
    <HTMLContainer className="canvas-embed" style={{ width: shape.props.w, height: shape.props.h }}>
      {embed ? (
        <iframe
          src={embed.embedUrl}
          title={embed.definition.title}
          width={shape.props.w}
          height={shape.props.h}
          // Embedded pages take the pointer only while the embed is being edited.
          style={{ border: 0, pointerEvents: editing ? "auto" : "none" }}
          sandbox="allow-scripts allow-same-origin allow-popups allow-presentation allow-forms"
          allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
        />
      ) : (
        <div className="canvas-embed-unsupported">
          <p>This website cannot be embedded.</p>
          {href && (
            <a data-canvas-interactive href={href} target="_blank" rel="noopener noreferrer" onPointerDown={(e) => e.stopPropagation()}>
              Open link
            </a>
          )}
        </div>
      )}
    </HTMLContainer>
  );
}
